import React, { useState, useEffect } from 'react';
import { Wallet, Loader2, RefreshCw, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import withdrawalService from '../../services/withdrawalService';

const AvailableBalance = ({ projectId, project, onRefresh }) => {
  const [allBalances, setAllBalances] = useState({});
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [expandedCurrency, setExpandedCurrency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAllBalances();
  }, [projectId]);

  const loadAllBalances = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await withdrawalService.getAllBalances(projectId);

      if (response.success && response.data?.balances) {
        const balances = response.data.balances;
        setAllBalances(balances);

        // Get currencies that have any balance (gross > 0 or available > 0)
        const currencies = Object.keys(balances).filter(
          curr => (balances[curr]?.gross > 0 || balances[curr]?.available > 0)
        );
        setAvailableCurrencies(currencies);

        // Notify parent of total available
        if (onRefresh) {
          const totalAvailable = Object.values(balances).reduce(
            (sum, b) => sum + (b?.available || 0), 0
          );
          onRefresh(totalAvailable);
        }
      }
    } catch (err) {
      console.error('Failed to load balances:', err);
      setError(err.message || 'Failed to load balances');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    loadAllBalances(true);
  };

  const toggleCurrencyExpand = (currency) => {
    setExpandedCurrency(expandedCurrency === currency ? null : currency);
  };

  const getCurrencyLabel = (currency) => {
    return currency === 'PI' ? 'π Pi' : currency === 'NGN' ? '₦ Naira' : currency;
  };

  if (loading) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-5 h-5 animate-spin text-teal-600" />
          <span className="ml-2 text-gray-600">Loading balances...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white border border-red-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <span className="text-sm">{error}</span>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>
    );
  }

  // No balances available
  if (availableCurrencies.length === 0) {
    return (
      <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-lg p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-200 rounded-lg">
              <Wallet className="w-5 h-5 text-gray-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700">Available to Withdraw</p>
              <p className="text-2xl font-bold text-gray-500">No funds yet</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            title="Refresh balance"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-600">
            Funds will appear here after donations are received.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Wallet className="w-5 h-5 text-teal-600" />
          <h3 className="font-semibold text-gray-900">Available Balances</h3>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-teal-600 hover:text-teal-700 hover:bg-teal-100 rounded-lg transition-colors disabled:opacity-50"
          title="Refresh balances"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Currency Balance Cards */}
      {availableCurrencies.map(currency => {
        const breakdown = allBalances[currency];
        const isExpanded = expandedCurrency === currency;

        return (
          <div
            key={currency}
            className={`border rounded-lg overflow-hidden transition-all ${currency === 'PI'
              ? 'bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-200'
              : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
              }`}
          >
            {/* Main Balance Display */}
            <div
              className="p-3 md:p-4 cursor-pointer"
              onClick={() => toggleCurrencyExpand(currency)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${currency === 'PI' ? 'bg-amber-100' : 'bg-green-100'
                    }`}>
                    <span className={`text-lg font-bold ${currency === 'PI' ? 'text-amber-600' : 'text-green-600'
                      }`}>
                      {currency === 'PI' ? 'π' : '₦'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{getCurrencyLabel(currency)}</p>
                    <p className={`text-xl md:text-2xl font-bold ${currency === 'PI' ? 'text-amber-600' : 'text-green-600'
                      }`}>
                      {withdrawalService.formatAmount(breakdown?.available || 0, currency)}
                    </p>
                  </div>
                </div>
                <div className={`p-1 rounded ${currency === 'PI' ? 'text-amber-600' : 'text-green-600'
                  }`}>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>
            </div>

            {/* Expanded Breakdown */}
            {isExpanded && breakdown && (
              <div className={`px-3 md:px-4 pb-3 md:pb-4 pt-2 border-t ${currency === 'PI' ? 'border-amber-200 bg-amber-50/50' : 'border-green-200 bg-green-50/50'
                }`}>
                <h4 className="text-xs font-semibold text-gray-700 mb-3 uppercase tracking-wide">
                  Balance Breakdown
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gross Raised:</span>
                    <span className="font-medium text-gray-900">
                      {withdrawalService.formatAmount(breakdown.gross, currency)}
                    </span>
                  </div>
                  {breakdown.fees > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Fees Deducted:</span>
                      <span>-{withdrawalService.formatAmount(breakdown.fees, currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Net Received:</span>
                    <span className="font-medium">{withdrawalService.formatAmount(breakdown.net, currency)}</span>
                  </div>
                  {breakdown.withdrawn > 0 && (
                    <div className="flex justify-between text-gray-500">
                      <span>Already Withdrawn:</span>
                      <span>-{withdrawalService.formatAmount(breakdown.withdrawn, currency)}</span>
                    </div>
                  )}
                  {breakdown.pending > 0 && (
                    <div className="flex justify-between text-yellow-600">
                      <span>Pending Withdrawals:</span>
                      <span>-{withdrawalService.formatAmount(breakdown.pending, currency)}</span>
                    </div>
                  )}
                  <div className={`flex justify-between pt-2 mt-2 border-t font-bold ${currency === 'PI' ? 'border-amber-200 text-amber-700' : 'border-green-200 text-green-700'
                    }`}>
                    <span>Available to Withdraw:</span>
                    <span>{withdrawalService.formatAmount(breakdown.available, currency)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Info Footer */}
      <div className="text-xs text-gray-500 text-center">
        Click on a currency to see detailed breakdown
      </div>
    </div>
  );
};

export default AvailableBalance;
